import { forwardRef, useEffect, useRef } from "react";
import { Button } from "../button";
import { Icon } from "../icons/icon";
import { Input } from "../input";

interface InlineInputControlProps {
  value: string;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  placeholder?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  error?: string;
  icon?: React.ReactNode;
}

export const InlineInputControl = forwardRef<HTMLInputElement, InlineInputControlProps>(
  ({ value, onChange, onSave, onCancel, placeholder, disabled, autoFocus = true, error, icon }, ref) => {
    const internalRef = useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;

    useEffect(() => {
      if (autoFocus) {
        const timer = setTimeout(() => {
          inputRef.current?.focus();
          inputRef.current?.select();
        }, 50);
        return () => clearTimeout(timer);
      }
    }, [autoFocus, inputRef]);

    useEffect(() => {
      if (error) {
        inputRef.current?.focus();
      }
    }, [error, inputRef]);

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        onCancel();
      } else if (e.key === "Enter") {
        e.preventDefault();
        onSave();
      }
    };

    return (
      <div className="space-y-1">
        <div className="flex gap-2 items-center">
          {icon && <span className="text-muted-foreground shrink-0">{icon}</span>}
          <Input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={handleKeyDown}
            onBlur={() => !disabled && !error && onCancel()}
            placeholder={placeholder}
            disabled={disabled}
            aria-invalid={!!error}
            className="flex-1 h-8 text-sm"
          />
          <Button
            size="icon-sm"
            variant="ghost"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onSave}
            disabled={disabled}
          >
            <Icon i="check" className="size-6 text-primary" />
          </Button>
        </div>
        {error && <p className="text-destructive text-sm">{error}</p>}
      </div>
    );
  },
);
InlineInputControl.displayName = "InlineInputControl";
